/**
 * Paint an AMG (or any short text) label onto the machine's top cap mesh
 * (`Glass_top`) by editing its texture directly. We:
 *
 *   1. Find the cap mesh and its current PBR material.
 *   2. Read the cap's UV bounding box from `geometry.attributes.uv`.
 *   3. Copy the existing baked texture into an offscreen canvas, then paint
 *      white + bold AMG text over the cap's UV region only.
 *   4. Clone the material (the texture is shared across multiple meshes via
 *      the `All_Mat` atlas), assign a CanvasTexture made from our canvas,
 *      and swap the cap's material to the clone.
 *
 * Result: the cap surface shows AMG instead of the baked snake logo, with
 * the original PBR shading (specular, normals, etc.) still applied. Other
 * meshes using `All_Mat` are untouched because they reference the original
 * material/texture.
 */
import {
  CanvasTexture,
  type Material,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  SRGBColorSpace,
  type Texture,
} from 'three';

const TARGET_MESH_NAME_PREFIX = 'glass_top';

interface LabelOptions {
  text: string;
  /** Background colour painted under the text. The original cap is white. */
  background?: string;
  /** Text colour. */
  foreground?: string;
  /**
   * If true, also fill the cap's full UV region with the background colour
   * before painting the text. Use this when the baked logo bleeds beyond the
   * text area. Defaults to true so the snake is fully covered.
   */
  paintFullRegion?: boolean;
}

export function addTopCapLabel(root: Object3D, opts: LabelOptions): void {
  const cap = findCapMesh(root);
  if (!cap) return;
  const mat = cap.material as Material;
  // Only standard/physical materials carry a `map` we can paint into.
  if (!('map' in mat) || !(mat as MeshStandardMaterial).map) return;
  const map = (mat as MeshStandardMaterial).map as Texture;
  const img = map.image as TexImageSource | undefined;
  if (!img || !('width' in img) || !('height' in img)) return;

  const W = (img as { width: number }).width;
  const H = (img as { height: number }).height;
  if (!W || !H) return;

  // UV bbox of just this cap mesh.
  const uvAttr = cap.geometry.attributes.uv;
  if (!uvAttr) return;
  let uMin = Infinity, vMin = Infinity, uMax = -Infinity, vMax = -Infinity;
  for (let i = 0; i < uvAttr.count; i++) {
    const u = uvAttr.getX(i);
    const v = uvAttr.getY(i);
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  }
  uMin = clamp01(uMin);
  uMax = clamp01(uMax);
  vMin = clamp01(vMin);
  vMax = clamp01(vMax);

  // GLB textures load with flipY:false — image rows match GL's bottom-origin
  // UV space directly, so a UV (u, v) maps to pixel (u*W, (1-v)*H) in canvas
  // coords. If the loader flipped Y (rare for GLBs), v maps straight through.
  const flipY = map.flipY;
  const px = Math.floor(uMin * W);
  const pw = Math.ceil((uMax - uMin) * W);
  const py = flipY
    ? Math.floor(vMin * H)
    : Math.floor((1 - vMax) * H);
  const ph = Math.ceil((vMax - vMin) * H);

  // Build the patched texture.
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  // Start from the existing texture so other UV regions of the cap atlas
  // remain unchanged (the cap UV bbox may not be perfectly tight).
  ctx.drawImage(img as CanvasImageSource, 0, 0, W, H);

  const background = opts.background ?? '#ffffff';
  const foreground = opts.foreground ?? '#0a0a12';
  if (opts.paintFullRegion !== false) {
    ctx.fillStyle = background;
    ctx.fillRect(px, py, pw, ph);
  }

  // Centre the text within the patched region. Letter-spacing manually so
  // AMG reads with a touch of breathing room.
  const fontSize = Math.max(12, Math.min(pw / 3.2, ph * 0.7));
  ctx.fillStyle = foreground;
  ctx.font = `900 ${fontSize}px -apple-system, "Segoe UI", Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const letters = opts.text.split('');
  const charWidths = letters.map((c) => ctx.measureText(c).width);
  const spacing = fontSize * 0.08;
  const totalWidth =
    charWidths.reduce((a, b) => a + b, 0) + spacing * (letters.length - 1);
  let x = px + pw / 2 - totalWidth / 2 + charWidths[0] / 2;
  const cy = py + ph / 2;
  for (let i = 0; i < letters.length; i++) {
    ctx.fillText(letters[i], x, cy);
    if (i < letters.length - 1) {
      x += charWidths[i] / 2 + spacing + charWidths[i + 1] / 2;
    }
  }

  // Build CanvasTexture, preserve the original texture's flipY + colorSpace
  // so it samples identically inside the shader.
  const patched = new CanvasTexture(canvas);
  patched.colorSpace = map.colorSpace ?? SRGBColorSpace;
  patched.flipY = flipY;
  patched.anisotropy = map.anisotropy ?? 1;
  patched.wrapS = map.wrapS;
  patched.wrapT = map.wrapT;
  patched.needsUpdate = true;

  // Clone the material so the texture swap stays local to the cap. The
  // original `All_Mat` material is shared across many meshes (tubes, base,
  // holders) — those still display unchanged.
  const newMat = (mat as MeshStandardMaterial).clone();
  newMat.map = patched;
  newMat.needsUpdate = true;
  cap.material = newMat;
}

function findCapMesh(root: Object3D): Mesh | null {
  let cap: Mesh | null = null;
  root.traverse((obj) => {
    if (cap) return;
    if (obj instanceof Mesh && obj.name.toLowerCase().startsWith(TARGET_MESH_NAME_PREFIX)) {
      cap = obj;
    }
  });
  return cap;
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
